import moment from 'moment';

const getDateMoment = date => moment(date);

const formatDate = date => moment(date).format('YYYY-MM-DD');

/**
 *
 * @param {string} checkIn
 * @param {string} checkOut
 * @returns {boolean} If checkIn and checkOut are set and match the expected booking engine conditions
 */
const isDateInvalid = (checkIn, checkOut, cutoff = 0) => {
    // You should use yesterday's date, because moment.js only compares dates. Therefore, the current day would be invalid.
    const firstBookableDay = new Date();
    firstBookableDay.setDate(firstBookableDay.getDate() - 1 + cutoff);

    const firstBookableDayMoment = getDateMoment(firstBookableDay);

    const checkinMoment = getDateMoment(checkIn);

    const checkoutMoment = getDateMoment(checkOut);

    return (!checkIn
        || !checkOut
        || checkinMoment.isSameOrBefore(firstBookableDayMoment)
        || checkoutMoment.isSameOrBefore(checkinMoment));
};

const sanitizeInlineDates = (checkIn, checkOut, los, cutoff) => {

    // Returning null so bootstrap dates are taken then
    if (!checkIn && !checkOut) {
        return null;
    }

    const firstBookableDay = new Date();
    firstBookableDay.setDate(firstBookableDay.getDate() - 1 + cutoff);
    const firstBookableDayMoment = getDateMoment(firstBookableDay);

    const checkinMoment = getDateMoment(checkIn);

    const checkoutMoment = getDateMoment(checkOut);

    // Only checkin is set inline

    if (!checkOut && checkIn && checkinMoment.isSameOrAfter(firstBookableDayMoment)) {
        return {
            checkIn,
            checkOut: formatDate(checkinMoment.add(los, 'days')),
        };
    }

    // Only checkout is set inline

    if (!checkIn && checkOut) {
        const calculatedChekinMatchingLos = checkoutMoment.clone().subtract(los, 'days');

        if (calculatedChekinMatchingLos.isSameOrAfter(firstBookableDayMoment)) {
            return {
                checkIn: formatDate(calculatedChekinMatchingLos),
                checkOut,
            };
        }
    }

    // Both are setted

    if (checkIn && checkOut) {
        // Both are valid
        if (checkinMoment.isSameOrAfter(firstBookableDayMoment) && checkoutMoment.isSameOrAfter(checkinMoment.clone().add(los, 'days'))) {
            return {
                checkIn,
                checkOut,
            };
        }

        // Only checkin is valid

        if (checkinMoment.isSameOrAfter(firstBookableDayMoment) && checkoutMoment.isBefore(checkinMoment.clone().add(los, 'days'))) {
            return {
                checkIn,
                checkOut: formatDate(checkinMoment.add(los, 'days')),
            };
        }

        // Only checkout is valid

        const calculatedChekinMatchingLos = checkoutMoment.clone().subtract(los, 'days');
        if (calculatedChekinMatchingLos.isSameOrAfter(firstBookableDayMoment)) {
            return {
                checkIn: formatDate(calculatedChekinMatchingLos),
                checkOut,
            };
        }
    }
    return null;
};

/**
 *
 * @param {string} checkIn Inline checkin
 * @param {string} checkOut Inline checkout
 * @param {{checkIn: string, checkOut: string}} backupDate Date from bootstrap
 * @param {number} los
 * @returns {{checkIn: string, checkOut: string}} Valid checkin and checkout
 */

const sanitizeDate = (checkIn, checkOut, backupDate, los, cutoff = 0) => {
    // Validate inline dates
    const sanitizedInlineDates = sanitizeInlineDates(checkIn, checkOut, los, cutoff);
    if (sanitizedInlineDates) {
        return sanitizedInlineDates;
    }

    // Validate bootstrap dates

    if (!isDateInvalid(backupDate.checkIn, backupDate.checkOut, los, cutoff)) {
        return backupDate;
    }

    // If both are invalid calculate them
    const firstBookableDay = moment().add(cutoff, 'days');

    return {
        checkIn  : firstBookableDay.format('YYYY-MM-DD'),
        checkOut : firstBookableDay.add(los, 'd').format('YYYY-MM-DD'),
    };
};

export default isDateInvalid;

export { sanitizeDate, formatDate, getDateMoment };
